import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider } from "./pages/context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./pages/routes/AdminRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import AppHeader from "./components/AppHeader";
import UpdatePopup from "./components/UpdatePopup";
import BackButtonHandler from "./components/BackButtonHandler";
import NetworkStatus from "./components/NetworkStatus";
import SplashScreen from "./components/SplashScreen";
import AppBanner from "./components/AppBanner";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

const LoginPage = lazy(() => import("./pages/LoginPage/LoginPage"));
const SignupForm = lazy(() => import("./pages/SignupForm"));
const ForgotPasswordPage = lazy(() => import("./pages/TopMenu/auth/ForgotPasswordPage"));
const SetPasswordPage = lazy(() => import("./pages/TopMenu/auth/SetPasswordPage"));
const ChangePasswordPage = lazy(() => import("./pages/TopMenu/auth/ChangePasswordPage"));
const HomePage = lazy(() => import("./pages/HomePage/HomePage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard/AdminDashboard"));
const SendNotification = lazy(() => import("./pages/AdminDashboard/SendNotification"));
const AddNoticeBoardPost = lazy(() => import("./pages/AdminDashboard/AddNoticeBoardPost"));
const ManageNotices = lazy(() => import("./pages/AdminDashboard/ManageNotices"));
const AddQuizAndGames = lazy(() => import("./pages/AdminDashboard/AddQuizAndGames"));
const AdminQuizSubmissions = lazy(() => import("./pages/AdminDashboard/AdminQuizSubmissions"));
const AdminLegalAndPolicies = lazy(() => import("./pages/AdminDashboard/AdminLegalAndPolicies"));
const Shiksha = lazy(() => import("./pages/Shiksha/Shiksha"));
const Rooms = lazy(() => import("./pages/Shiksha/Rooms/Rooms"));
const Coachings = lazy(() => import("./pages/Shiksha/Coachings/Coachings"));
const Mess = lazy(() => import("./pages/Shiksha/Mess/Mess"));
const Shops = lazy(() => import("./pages/Shiksha/Shops/Shops"));
const ClassesAndNotes = lazy(() => import("./pages/Shiksha/ClassesAndNotes"));
const CurrentNews = lazy(() => import("./pages/Shiksha/CurrentNews"));
const SarvagyaConnect = lazy(() => import("./pages/SarvagyaConnect/SarvagyaConnect"));
const CommentSection = lazy(() => import("./pages/SarvagyaConnect/CommentSection"));
const MuddaDetail = lazy(() => import("./pages/SarvagyaConnect/MuddaDetail"));
const QuizAndGames = lazy(() => import("./pages/QuizAndGames/QuizAndGames"));
const GameDetails = lazy(() => import("./pages/QuizAndGames/GameDetails"));
const Sudoku = lazy(() => import("./pages/QuizAndGames/games/Sudoku"));
const WordSearch = lazy(() => import("./pages/QuizAndGames/games/WordSearch"));
const DinoRun = lazy(() => import("./pages/QuizAndGames/games/DinoRun"));
const BrickBlast = lazy(() => import("./pages/QuizAndGames/games/BrickBlast"));
const MathQuiz = lazy(() => import("./pages/QuizAndGames/games/MathQuiz"));
const SupportStudents = lazy(() => import("./pages/SupportStudents/SupportStudents"));
const HelpAndSupport = lazy(() => import("./pages/HelpAndSupport/HelpAndSupport"));
const LegalAndPolicies = lazy(() => import("./pages/LegalAndPolicies/LegalAndPolicies"));
const ConnectWithUs = lazy(() => import("./pages/ConnectWithUs/ConnectWithUs"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage/UserProfilePage"));
const NotificationPage = lazy(() => import("./pages/Notification/NotificationPage"));
const RateAndRefer = lazy(() => import("./pages/RateAndRefer/RateAndRefer"));
const Sangeet = lazy(() => import("./pages/Sangeet/Sangeet"));
const SubscriptionPage = lazy(() => import("./components/SubscriptionPage"));
const StudentMissionPage = lazy(() => import("./components/StudentMissionPage"));
const GlobalSearch = lazy(() => import("./pages/HomePage/GlobalSearch"));
const YogaAndMeditation = lazy(() => import("./pages/YogaAndMeditation/YogaAndMeditation"));
const Yoga = lazy(() => import("./pages/YogaAndMeditation/Yoga"));
const Meditation = lazy(() => import("./pages/YogaAndMeditation/Meditation"));

const NotAuthorized = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
    <div className="text-6xl mb-4">⛔</div>
    <h1 className="text-2xl font-bold text-red-600 mb-2">अनुमति नहीं है</h1>
    <p className="text-gray-600 text-center mb-6">इस पेज को देखने की आपके पास अनुमति नहीं है।</p>
    <a href="/home" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">होम पर जाएं</a>
  </div>
);

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-3">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-500 text-sm">लोड हो रहा है...</p>
    </div>
  </div>
);

function DeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const handleDeepLink = (event) => {
      try {
        const url = new URL(event.url);
        const path = url.pathname + url.search;
        navigate(path, { replace: true });
      } catch {}
    };
    let listenerHandle;
    CapApp.addListener("appUrlOpen", handleDeepLink).then(handle => {
      listenerHandle = handle;
    });
    return () => {
      if (listenerHandle) listenerHandle.remove();
    };
  }, [navigate]);

  return null;
}

function AppRoutes() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  if (showSplash) return <SplashScreen />;

  return (
    <>
      <DeepLinkHandler />
      <AppBanner />
      <AppHeader />
      <UpdatePopup />
      <NetworkStatus />
      <BackButtonHandler />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupForm />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/not-authorized" element={<NotAuthorized />} />
          <Route path="/HelpAndSupport" element={<HelpAndSupport />} />
          <Route path="/SupportStudents" element={<SupportStudents />} />
          <Route path="/LegalAndPolicies" element={<LegalAndPolicies />} />

          <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          <Route path="/set-password" element={<ProtectedRoute><SetPasswordPage /></ProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />

          <Route path="/admin-dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/send-notification" element={<AdminRoute><SendNotification /></AdminRoute>} />
          <Route path="/add-notice" element={<AdminRoute><AddNoticeBoardPost /></AdminRoute>} />
          <Route path="/manage-notices" element={<AdminRoute><ManageNotices /></AdminRoute>} />
          <Route path="/add-quiz" element={<AdminRoute><AddQuizAndGames /></AdminRoute>} />
          <Route path="/view-submissions" element={<AdminRoute><AdminQuizSubmissions /></AdminRoute>} />
          <Route path="/admin-legal-policies" element={<AdminRoute><AdminLegalAndPolicies /></AdminRoute>} />

          <Route path="/Shiksha" element={<ProtectedRoute><Shiksha /></ProtectedRoute>} />
          <Route path="/Rooms" element={<ProtectedRoute><Rooms /></ProtectedRoute>} />
          <Route path="/Coachings" element={<ProtectedRoute><Coachings /></ProtectedRoute>} />
          <Route path="/Mess" element={<ProtectedRoute><Mess /></ProtectedRoute>} />
          <Route path="/Shops" element={<ProtectedRoute><Shops /></ProtectedRoute>} />
          <Route path="/ClassesAndNotes" element={<ProtectedRoute><ClassesAndNotes /></ProtectedRoute>} />
          <Route path="/CurrentNews" element={<ProtectedRoute><CurrentNews /></ProtectedRoute>} />

          <Route path="/SarvagyaConnect" element={<ProtectedRoute><SarvagyaConnect /></ProtectedRoute>} />
          <Route path="/posts/:postId" element={<ProtectedRoute><CommentSection /></ProtectedRoute>} />
          <Route path="/notes/:postId" element={<ProtectedRoute><CommentSection /></ProtectedRoute>} />
          <Route path="/notifications/:postId" element={<ProtectedRoute><CommentSection /></ProtectedRoute>} />
          <Route path="/alerts/:postId" element={<ProtectedRoute><CommentSection /></ProtectedRoute>} />
          <Route path="/mudda/:muddaId" element={<ProtectedRoute><MuddaDetail /></ProtectedRoute>} />

          <Route path="/GlobalSearch" element={<ProtectedRoute><GlobalSearch /></ProtectedRoute>} />

          <Route path="/QuizAndGames" element={<ProtectedRoute><QuizAndGames /></ProtectedRoute>} />
          <Route path="/GameDetails/:gameId" element={<ProtectedRoute><GameDetails /></ProtectedRoute>} />
          <Route path="/games/sudoku" element={<ProtectedRoute><Sudoku /></ProtectedRoute>} />
          <Route path="/games/wordsearch" element={<ProtectedRoute><WordSearch /></ProtectedRoute>} />
          <Route path="/games/dinorun" element={<ProtectedRoute><DinoRun /></ProtectedRoute>} />
          <Route path="/games/brickblast" element={<ProtectedRoute><BrickBlast /></ProtectedRoute>} />
          <Route path="/games/mathquiz" element={<ProtectedRoute><MathQuiz /></ProtectedRoute>} />

          <Route path="/UserProfile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
          <Route path="/Notifications" element={<ProtectedRoute><NotificationPage /></ProtectedRoute>} />
          <Route path="/RateAndRefer" element={<ProtectedRoute><RateAndRefer /></ProtectedRoute>} />

          <Route path="/ConnectWithUs" element={<ProtectedRoute><ConnectWithUs /></ProtectedRoute>} />

          <Route path="/Sangeet" element={<ProtectedRoute><Sangeet /></ProtectedRoute>} />
          <Route path="/Subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
          <Route path="/StudentMission" element={<ProtectedRoute><StudentMissionPage /></ProtectedRoute>} />

          <Route path="/YogaAndMeditation" element={<ProtectedRoute><YogaAndMeditation /></ProtectedRoute>} />
          <Route path="/yoga" element={<ProtectedRoute><Yoga /></ProtectedRoute>} />
          <Route path="/meditation" element={<ProtectedRoute><Meditation /></ProtectedRoute>} />

          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;